import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import { History } from 'lucide-react';
import * as React from 'react';

import { ApiError } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import { refusalMessage } from '../refusal';
import { absent } from './account-facts';
import { Pager, PastTheEnd, useAdminData } from './admin-page';
import { TableFrame } from './table-frame';

/**
 * One change to a value, in the popover's shape. The advisor record page's four history reads —
 * the response timeline, internal-field events, figure events and ranking events — share no row
 * shape (their values are strings, `{t,v}` payloads, bare numbers or `{place, territory}`, and
 * their actors are an email, a tagged actor or nothing), so each consumer's normaliser turns its
 * rows into this, and `who`, `from` and `to` are nodes it has already rendered.
 */
export interface IHistoryRow {
  /** Unique within a page — the event's id, as sent. */
  key: string;
  /** When it happened; `null` is a legacy row with no date, drawn "Undated". */
  at: Nullable<string>;
  who: React.ReactNode;
  /** What changed, where a route says it (a figure's metric, a field's part). */
  what?: React.ReactNode;
  from: React.ReactNode;
  to: React.ReactNode;
  /** Only some routes record one (a correction's, a figure's); internal-field events never do. */
  reason?: Nullable<string>;
  /** A row the popover's `hide` switch takes off the page — the timeline's sync changes. */
  hideable?: boolean;
}

/** A page of a history read, as every one of the four routes pages it. */
export interface IHistoryPage {
  items: IHistoryRow[];
  page: number;
  pageSize: number;
  total: number;
  /** What the route says beside its rows, drawn above them on every page — an internal field's
   *  values as stored now (F2's R13), which no row describes. Never merged into the rows. */
  above?: React.ReactNode;
}

/**
 * A value's history, behind a button beside it (the advisor record page's roadmap, K7; the F1 kit
 * plan's R11). It draws what it is handed and judges nothing.
 *
 * - **Rows in the order sent.** Every route sends newest first, and this side sorts nothing.
 * - **Columns only where a row fills them.** What is drawn when a row says what changed, and
 *   Reason when a row carries one — an internal field's events never do, and an empty column
 *   would read as reasons withheld.
 * - **Read afresh on every opening**, from page 1, because a write since the last opening may
 *   have added a row; the popover's body is unmounted when it closes, which aborts a read still
 *   out (`useAdminData`).
 * - **Three arms, and the loading one is text** ("Reading…"), never the kit's skeleton: a popover
 *   renders into `document.body`, outside every section a spec could scope a skeleton count to.
 * - **An empty page of a history that is not empty is past its end** (`PastTheEnd`), as on every
 *   admin list; `emptyText` is the caller's, since "no change recorded" means different things —
 *   F2's migrated values carry a stored value and no event, and say so.
 * - **`above` is drawn above the rows, never among them** — what the route sends beside its
 *   events (F2's "Stored now"), on every page and over the empty arm alike.
 * - **`hide` takes `hideable` rows off the page read, and says how many** — the response timeline's
 *   sync changes (SR-222's block 9), hidden at each opening. It filters a page, not the history:
 *   the pager stays the route's, so a page can hold fewer rows than it counts.
 *
 * A refused read is said in survey-api's words (`refusalMessage`). It lives in the admin domain
 * rather than `src/design` for that reason and for `Pager`: it says a failure itself.
 */
export function HistoryPopover({
  label,
  name,
  read,
  emptyText,
  hide
}: {
  /** The value's label — "AUM". */
  label: string;
  /** Whose value it is — the participant's name. */
  name: string;
  read: (page: number, signal: AbortSignal) => Promise<IHistoryPage>;
  emptyText: string;
  /** A switch, on at each opening, taking the `hideable` rows off the page read — drawn only over
   *  a page holding one. Its `label` is the switch's name. */
  hide?: { label: string };
}) {
  const title = `History of ${label} for ${name}`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="icon" className="size-7" aria-label={title}>
          <History className="size-4" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label={title}
        className="grid max-h-[min(32rem,70vh)] w-[min(44rem,calc(100vw-2rem))] gap-3 overflow-y-auto p-3 text-sm"
      >
        <HistoryBody title={title} read={read} emptyText={emptyText} hide={hide} />
      </PopoverContent>
    </Popover>
  );
}

function HistoryBody({
  title,
  read,
  emptyText,
  hide
}: {
  title: string;
  read: (page: number, signal: AbortSignal) => Promise<IHistoryPage>;
  emptyText: string;
  hide?: { label: string };
}) {
  const [page, setPage] = React.useState(1);
  const [hiding, setHiding] = React.useState(true);
  const { data, error, reload } = useAdminData(async (signal) => {
    try {
      return await read(page, signal);
    } catch (readError) {
      if (signal.aborted) throw readError;
      // Only survey-api's refusal has words for a reader; anything else — a dropped connection, a
      // consumer's normaliser that threw — is logged and said plainly, never as its raw message.
      if (!(readError instanceof ApiError)) {
        console.error('[HistoryPopover] the history could not be read:', readError);
        throw new Error('Could not read the history.');
      }
      throw new Error(refusalMessage(readError, 'Could not read the history'));
    }
  }, [page]);
  // The Activity's rule: the pager is drawn from the page last read, so a press for the page
  // already asked for — after its read failed — asks again rather than setting what is set.
  const turnTo = (target: number) => (target === page ? void reload() : setPage(target));

  if (data === null) {
    return error ? (
      <p role="alert" className="text-destructive">
        {error}
      </p>
    ) : (
      <p className="text-muted-foreground">Reading…</p>
    );
  }

  const hideable = hide !== undefined && data.items.some((row) => row.hideable);
  const shown = hideable && hiding ? data.items.filter((row) => !row.hideable) : data.items;
  const withWhat = shown.some((row) => row.what !== undefined);
  const withReason = shown.some((row) => row.reason != null);
  return (
    <>
      {error && (
        <p role="alert" className="text-destructive">
          Could not read that page — showing the history as last read. {error}
        </p>
      )}
      {data.above}
      {hideable && (
        <HiddenRows
          label={hide.label}
          hiding={hiding}
          onHiding={setHiding}
          hidden={data.items.length - shown.length}
        />
      )}
      {data.items.length === 0 && (
        <p className="text-muted-foreground">
          {data.total > 0 ? (
            <PastTheEnd
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              counted={['change', 'changes']}
              onPage={turnTo}
            />
          ) : (
            emptyText
          )}
        </p>
      )}
      {shown.length > 0 && (
        <TableFrame>
          <Table aria-label={title}>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Who</TableHead>
                {withWhat && <TableHead>What</TableHead>}
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                {withReason && <TableHead>Reason</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((row) => (
                <TableRow key={row.key}>
                  <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                    {row.at === null ? absent('Undated') : formatDateTime(row.at)}
                  </TableCell>
                  <TableCell className="break-all">{row.who}</TableCell>
                  {withWhat && <TableCell>{row.what}</TableCell>}
                  <TableCell>{row.from}</TableCell>
                  <TableCell>{row.to}</TableCell>
                  {withReason && <TableCell className="whitespace-pre-line">{row.reason}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableFrame>
      )}
      <Pager page={data.page} pageSize={data.pageSize} total={data.total} onPage={turnTo} />
    </>
  );
}

/**
 * The `hide` switch, and what it took off this page — said, never silent, since the page then holds
 * fewer rows than its pager counts. The Timeline tab draws the same switch over its own table.
 */
export function HiddenRows({
  label,
  hiding,
  onHiding,
  hidden
}: {
  label: string;
  hiding: boolean;
  onHiding: (hiding: boolean) => void;
  /** How many rows of the page read the switch took off. */
  hidden: number;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
      <label className="flex items-center gap-2">
        <Switch checked={hiding} onCheckedChange={onHiding} />
        {label}
      </label>
      {hiding && hidden > 0 && (
        <span className="text-xs text-muted-foreground">
          {hidden === 1 ? '1 row hidden on this page' : `${hidden} rows hidden on this page`}
        </span>
      )}
    </div>
  );
}
